/**
 * CardAssassinOverlay - Cinematic "Card Assassin" Overtake Animation Overlay
 * Adapted from ideas/card-assassin-8-weapons-2.html.
 *
 * Displays a transparent modal overlay featuring walking card IK, Verlet ragdoll physics,
 * and randomly equipped arsenal weaponry when a player passes another player in the show phase.
 */

const WEAPONS = {
  sword: { carry: 18, coil: -58, strike: 34, recover: 10, reach: 0 },
  mace:  { carry: 26, coil: -72, strike: 50, recover: 18, reach: 0 },
  spear: { carry: 8,  coil: -42, strike: -8, recover: 6,  reach: 0 },
  club:  { carry: 28, coil: -78, strike: 54, recover: 20, reach: 0 },
  bat:   { carry: 24, coil: -80, strike: 48, recover: 14, reach: 6 },
  axe:   { carry: 22, coil: -82, strike: 56, recover: 16, reach: 4 },
  dagger:{ carry: 12, coil: -28, strike: 18, recover: 8,  reach: 10 },
  bow:   { carry: -8, coil: -12, strike: -4, recover: -6, reach: 0 }
};
const RANGED = { spear: true, bow: true };
const WEAPON_KEYS = Object.keys(WEAPONS);

const START_LEFT = 0.11;
const WALK_SPEED = 0.16;
const STAND_LIFT = 34;
const STRIKE_GAP = 96;
const THIGH = 26;
const SHIN = 22;
const LEG_SPAN = THIGH + SHIN;
const STEP_LIFT = 14;

function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function easeOut(t) { return 1 - Math.pow(1 - t, 3); }

function hashNoise(t) {
  const n = Math.sin(t * 12.9898) * 43758.5453;
  return n - Math.floor(n);
}
function valueNoise(t) {
  const i = Math.floor(t);
  const f = t - i;
  const u = f * f * (3 - 2 * f);
  return lerp(hashNoise(i), hashNoise(i + 1), u) * 2 - 1;
}

function Spring(freq, zeta, pos) {
  this.omega = Math.PI * 2 * freq;
  this.zeta = zeta;
  this.pos = pos || 0;
  this.vel = 0;
}
Spring.prototype.tick = function (target, dt) {
  const maxStep = 0.004; // 4ms fixed sub-steps guarantee numerical stability for high-frequency springs
  let remaining = Math.min(Math.max(dt, 0.001), 0.05);
  while (remaining > 0) {
    const step = Math.min(remaining, maxStep);
    const w = this.omega;
    const acc = (target - this.pos) * w * w - 2 * this.zeta * w * this.vel;
    this.vel += acc * step;
    this.pos += this.vel * step;
    remaining -= step;
  }
  if (!Number.isFinite(this.pos)) {
    this.pos = target;
    this.vel = 0;
  }
  return this.pos;
};

let _pid = 0;
function VPoint(x, y, mass, group) {
  return { id: ++_pid, x, y, px: x, py: y, mass: mass || 1, pin: false, group: group || 'free', radius: 7 };
}

function aabbOf(p) {
  const r = p.radius;
  return { x0: p.x - r, y0: p.y - r, x1: p.x + r, y1: p.y + r };
}
function aabbUnion(a, b) {
  return {
    x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0),
    x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1)
  };
}
function aabbHits(a, b) {
  return a.x0 <= b.x1 && a.x1 >= b.x0 && a.y0 <= b.y1 && a.y1 >= b.y0;
}
function aabbArea(a) {
  return Math.max(0, a.x1 - a.x0) * Math.max(0, a.y1 - a.y0);
}
function aabbSurface(a) {
  return 2 * (Math.max(0, a.x1 - a.x0) + Math.max(0, a.y1 - a.y0));
}

function BVH() {
  this.root = null;
  this.leaves = [];
}
BVH.prototype.build = function (points) {
  const leaves = points.map((p) => ({ leaf: true, aabb: aabbOf(p), ref: p, left: null, right: null }));
  this.leaves = leaves;
  this.root = leaves.length ? this._split(leaves) : null;
  return this.root;
};
BVH.prototype._split = function (nodes) {
  if (nodes.length === 1) return nodes[0];
  if (nodes.length === 2) {
    return {
      leaf: false,
      aabb: aabbUnion(nodes[0].aabb, nodes[1].aabb),
      left: nodes[0],
      right: nodes[1]
    };
  }
  let best = null;
  for (const axis of ['x', 'y']) {
    const sorted = nodes.slice().sort((a, b) => {
      const ca = axis === 'x' ? a.aabb.x0 + a.aabb.x1 : a.aabb.y0 + a.aabb.y1;
      const cb = axis === 'x' ? b.aabb.x0 + b.aabb.x1 : b.aabb.y0 + b.aabb.y1;
      return ca - cb;
    });
    let leftBox = sorted[0].aabb;
    const prefix = [leftBox];
    for (let i = 1; i < sorted.length - 1; i++) {
      leftBox = aabbUnion(leftBox, sorted[i].aabb);
      prefix[i] = leftBox;
    }
    let rightBox = sorted[sorted.length - 1].aabb;
    for (let i = sorted.length - 1; i >= 1; i--) {
      if (i < sorted.length - 1) rightBox = aabbUnion(rightBox, sorted[i].aabb);
      const leftCount = i;
      const rightCount = sorted.length - i;
      const cost = aabbSurface(prefix[i - 1]) * leftCount + aabbSurface(rightBox) * rightCount;
      if (!best || cost < best.cost) {
        best = { cost, axis, index: i, sorted };
      }
    }
  }
  const mid = best ? best.index : (nodes.length >> 1);
  const ordered = best ? best.sorted : nodes;
  const left = this._split(ordered.slice(0, mid));
  const right = this._split(ordered.slice(mid));
  return { leaf: false, aabb: aabbUnion(left.aabb, right.aabb), left, right };
};
BVH.prototype.refit = function () {
  if (!this.root) return;
  (function walk(node) {
    if (node.leaf) {
      node.aabb = aabbOf(node.ref);
      return node.aabb;
    }
    const l = walk(node.left);
    const r = walk(node.right);
    node.aabb = aabbUnion(l, r);
    return node.aabb;
  })(this.root);
};
BVH.collide = function (a, b, fn) {
  function walk(n0, n1) {
    if (!n0 || !n1 || !aabbHits(n0.aabb, n1.aabb)) return;
    if (n0.leaf && n1.leaf) { fn(n0.ref, n1.ref); return; }
    if (n0.leaf) { walk(n0, n1.left); walk(n0, n1.right); return; }
    if (n1.leaf) { walk(n0.left, n1); walk(n0, n1.right); return; }
    if (aabbArea(n0.aabb) > aabbArea(n1.aabb)) {
      walk(n0.left, n1);
      walk(n0.right, n1);
    } else {
      walk(n0, n1.left);
      walk(n0, n1.right);
    }
  }
  if (a && b) walk(a.root, b.root);
};
BVH.prototype.boxes = function () {
  const list = [];
  (function walk(node, depth) {
    if (!node) return;
    list.push({ aabb: node.aabb, leaf: node.leaf, depth });
    if (!node.leaf) {
      walk(node.left, depth + 1);
      walk(node.right, depth + 1);
    }
  })(this.root, 0);
  return list;
};

function collideParticles(a, b) {
  if (a.group === b.group) return;
  const minDist = a.radius + b.radius;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d2 = dx * dx + dy * dy;
  if (d2 >= minDist * minDist || d2 === 0) return;
  const d = Math.sqrt(d2);
  const nx = dx / d;
  const ny = dy / d;
  const overlap = minDist - d;
  const invA = a.pin ? 0 : 1 / a.mass;
  const invB = b.pin ? 0 : 1 / b.mass;
  const inv = invA + invB;
  if (inv <= 0) return;
  const corr = overlap / inv;
  a.x -= nx * corr * invA;
  a.y -= ny * corr * invA;
  b.x += nx * corr * invB;
  b.y += ny * corr * invB;
}

function VRod(a, b, rest, stiff) {
  return { a, b, rest: rest == null ? Math.hypot(b.x - a.x, b.y - a.y) : rest, stiff: stiff == null ? 1 : stiff };
}
function verletStep(p, dt, gx, gy, damp) {
  if (p.pin) { p.px = p.x; p.py = p.y; return; }
  const vx = (p.x - p.px) * damp;
  const vy = (p.y - p.py) * damp;
  p.px = p.x;
  p.py = p.y;
  p.x += vx + gx * dt * dt;
  p.y += vy + gy * dt * dt;
}
function verletImpulse(p, ix, iy) {
  p.px -= ix;
  p.py -= iy;
}
function solveRod(c) {
  const dx = c.b.x - c.a.x;
  const dy = c.b.y - c.a.y;
  const dist = Math.hypot(dx, dy) || 0.0001;
  const frac = (c.rest - dist) / dist;
  const invA = c.a.pin ? 0 : 1 / c.a.mass;
  const invB = c.b.pin ? 0 : 1 / c.b.mass;
  const inv = invA + invB;
  if (inv <= 0) return;
  const k = frac * c.stiff / inv;
  const ox = dx * k;
  const oy = dy * k;
  c.a.x -= ox * invA;
  c.a.y -= oy * invA;
  c.b.x += ox * invB;
  c.b.y += oy * invB;
}
function collideFloor(p, floorY, bounce) {
  if (p.y > floorY) {
    const vy = p.y - p.py;
    p.y = floorY;
    p.py = p.y + vy * bounce;
    p.x += (p.px - p.x) * 0.18;
  }
}
function collideWalls(p, x0, x1) {
  if (p.x < x0) { p.px = p.x + (p.x - p.px) * 0.4; p.x = x0; }
  if (p.x > x1) { p.px = p.x + (p.x - p.px) * 0.4; p.x = x1; }
}

function solveLeg(targetX, targetY, kneeDir, legThigh = THIGH, legShin = SHIN) {
  let dx = targetX;
  let dy = targetY;
  let dist = Math.hypot(dx, dy) || 0.0001;
  const legSpan = legThigh + legShin;
  const minD = Math.abs(legThigh - legShin) + 0.5;
  const maxD = legSpan - 0.4;
  const reach = clamp(dist, minD, maxD);
  dx *= reach / dist;
  dy *= reach / dist;
  dist = reach;

  const base = Math.atan2(dx, dy);
  const cosHip = clamp((legThigh * legThigh + dist * dist - legShin * legShin) / (2 * legThigh * dist), -1, 1);
  const hipBend = Math.acos(cosHip);
  const thighRad = base - kneeDir * hipBend;

  const kneeX = Math.sin(thighRad) * legThigh;
  const kneeY = Math.cos(thighRad) * legThigh;
  const shinWorld = Math.atan2(targetX - kneeX, targetY - kneeY);
  const shinLocal = shinWorld - thighRad;
  const footLocal = -shinWorld + 0.16;

  return {
    thigh: thighRad * 180 / Math.PI,
    shin: shinLocal * 180 / Math.PI,
    foot: footLocal * 180 / Math.PI
  };
}

let activeOverlayInstance = null;

export class CardAssassinOverlay {
  /**
   * Displays the Card Assassin animation in a transparent overlay.
   * Randomly selects one of the weapons in the arsenal.
   *
   * @param {Object} [options]
   * @param {string} [options.passerName] - Name of player who passed
   * @param {string} [options.passedName] - Name of player passed
   * @param {Object} [options.passerCard] - { r: 'A', s: 'S' }
   * @param {Object} [options.passedCard] - { r: 'K', s: 'H' }
   * @param {string} [options.weapon] - Optional specific weapon key
   * @param {Function} [options.onComplete] - Callback on dismiss or completion
   * @returns {CardAssassinOverlay|null}
   */
  static show(options = {}) {
    const settings = options.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings && (settings.cardAssassin === 'no' || settings.cardAssassin === 'off' || settings.cardAssassin === false)) {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (typeof document === 'undefined') {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (activeOverlayInstance) {
      activeOverlayInstance.close(false);
    }

    const instance = new CardAssassinOverlay(options);
    activeOverlayInstance = instance;
    instance.mount();
    return instance;
  }

  static hide() {
    if (activeOverlayInstance) {
      activeOverlayInstance.close(true);
    }
  }

  constructor(options = {}) {
    this.options = options;
    this.onComplete = options.onComplete || (() => {});
    this.raf = 0;
    this.running = false;
    this.currentWeapon = options.weapon && WEAPONS[options.weapon] ? options.weapon : this.pickRandomWeapon();
    this.overlayEl = null;
    this.autoCloseTimer = null;
    this.safetyTimer = null;
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  pickRandomWeapon() {
    return WEAPON_KEYS[Math.floor(Math.random() * WEAPON_KEYS.length)];
  }

  formatCard(card, fallbackRank, fallbackSuit) {
    if (!card) {
      return { r: fallbackRank, s: fallbackSuit, isRed: fallbackSuit === '♥' || fallbackSuit === '♦' };
    }
    if (Array.isArray(card) && card.length > 0) {
      card = card[card.length - 1];
    }
    if (typeof card === 'string') {
      const match = card.match(/^([0-9AJQK]+)([HDSC♥♦♠♣]?)$/i);
      if (match) {
        card = { r: match[1], s: match[2] };
      }
    }
    let r = card.r !== undefined ? card.r : (card.rank !== undefined ? card.rank : fallbackRank);
    if (r === 1 || r === '1') r = 'A';
    else if (r === 11 || r === '11') r = 'J';
    else if (r === 12 || r === '12') r = 'Q';
    else if (r === 13 || r === '13') r = 'K';
    r = String(r).toUpperCase();

    let s = card.s || card.suit || fallbackSuit;
    if (s === 'H' || s === 'h') s = '♥';
    else if (s === 'D' || s === 'd') s = '♦';
    else if (s === 'S' || s === 's') s = '♠';
    else if (s === 'C' || s === 'c') s = '♣';
    const isRed = s === '♥' || s === '♦';
    return { r, s, isRed };
  }

  buildHtml() {
    const passer = this.options.passerName || 'Player';
    const passed = this.options.passedName || 'Opponent';
    const killerCard = this.formatCard(this.options.passerCard, 'A', '♠');
    const victimCard = this.formatCard(this.options.passedCard, 'K', '♥');

    return `
      <div class="assassin-banner">
        <span class="assassin-badge">⚔️ OVERTAKE!</span>
        <h2 class="assassin-title">${passer} passes ${passed}!</h2>
      </div>

      <div class="stage" id="assassinStage">
        <div class="card" id="killer" style="color: ${killerCard.isRed ? 'var(--red)' : 'var(--ink)'};">
          <div class="face">
            <div class="corner"><span class="r">${killerCard.r}</span><span class="s">${killerCard.s}</span></div>
            <div class="pip">${killerCard.s}</div>
            <div class="corner br"><span class="r">${killerCard.r}</span><span class="s">${killerCard.s}</span></div>
          </div>
          <div class="weapon" id="w-sword" data-kind="sword">
            <div class="sword-pommel"></div>
            <div class="sword-grip"></div>
            <div class="sword-guard"></div>
            <div class="sword-blade"></div>
          </div>
          <div class="weapon" id="w-mace" data-kind="mace">
            <div class="sword-grip"></div>
            <div class="mace-haft"></div>
            <div class="mace-head"></div>
          </div>
          <div class="weapon" id="w-spear" data-kind="spear">
            <div class="spear-haft"></div>
            <div class="spear-head"></div>
          </div>
          <div class="weapon" id="w-club" data-kind="club">
            <div class="sword-grip"></div>
            <div class="club-body"></div>
          </div>
          <div class="weapon" id="w-bat" data-kind="bat">
            <div class="bat-knob"></div>
            <div class="bat-handle"></div>
            <div class="bat-barrel"></div>
          </div>
          <div class="weapon" id="w-axe" data-kind="axe">
            <div class="sword-grip"></div>
            <div class="axe-haft"></div>
            <div class="axe-bit"></div>
          </div>
          <div class="weapon" id="w-dagger" data-kind="dagger">
            <div class="sword-pommel"></div>
            <div class="sword-grip"></div>
            <div class="dagger-guard"></div>
            <div class="dagger-blade"></div>
          </div>
          <div class="weapon" id="w-bow" data-kind="bow">
            <div class="bow-limb"></div>
            <div class="bow-string"></div>
          </div>
          <div class="legs">
            <div class="leg l" id="legL">
              <div class="thigh" id="thighL">
                <div class="shin" id="shinL">
                  <div class="foot" id="footL"></div>
                </div>
              </div>
            </div>
            <div class="leg r" id="legR">
              <div class="thigh" id="thighR">
                <div class="shin" id="shinR">
                  <div class="foot" id="footR"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card" id="victim" style="color: ${victimCard.isRed ? 'var(--red)' : 'var(--ink)'};">
          <div class="face">
            <div class="corner"><span class="r">${victimCard.r}</span><span class="s">${victimCard.s}</span></div>
            <div class="pip">${victimCard.s}</div>
            <div class="corner br"><span class="r">${victimCard.r}</span><span class="s">${victimCard.s}</span></div>
            <div class="wound"></div>
            <div class="x-eyes">✕ ✕</div>
          </div>
        </div>

        <svg id="bvhLayer" class="hidden" aria-hidden="true"></svg>
        <div class="splash a"></div>
        <div class="splash b"></div>
        <div class="splash c"></div>
        <div id="spear-flight">
          <div class="spear-haft"></div>
          <div class="spear-head"></div>
        </div>
        <div id="arrow-flight">
          <div class="arrow-shaft"></div>
          <div class="arrow-head"></div>
        </div>
      </div>
    `;
  }

  mount() {
    let el = document.getElementById('cardAssassinOverlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cardAssassinOverlay';
      el.className = 'card-assassin-overlay';
      document.body.appendChild(el);
    }
    el.innerHTML = this.buildHtml();
    this.overlayEl = el;

    // Element references
    this.stage = el.querySelector('#assassinStage');
    this.killer = el.querySelector('#killer');
    this.victim = el.querySelector('#victim');
    this.spearFlight = el.querySelector('#spear-flight');
    this.arrowFlight = el.querySelector('#arrow-flight');
    this.bvhLayer = el.querySelector('#bvhLayer');
    this.bones = {
      L: {
        thigh: el.querySelector('#thighL'),
        shin: el.querySelector('#shinL'),
        foot: el.querySelector('#footL')
      },
      R: {
        thigh: el.querySelector('#thighR'),
        shin: el.querySelector('#shinR'),
        foot: el.querySelector('#footR')
      }
    };

    // Dismiss on clicking anywhere to skip
    el.addEventListener('click', () => {
      this.close(true);
    });

    window.addEventListener('keydown', this.handleKeyDown);

    // Apply randomly selected weapon
    this.setWeapon(this.currentWeapon);

    // Show overlay and start simulation
    requestAnimationFrame(() => {
      el.classList.add('active');
      this.play();
    });
  }

  handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.close(true);
    }
  }

  setWeapon(kind) {
    this.currentWeapon = WEAPONS[kind] ? kind : 'sword';
    if (!this.overlayEl) return;
    this.overlayEl.querySelectorAll('.weapon').forEach((el) => {
      el.classList.toggle('active', el.dataset.kind === this.currentWeapon);
      el.classList.remove('firing');
    });
    this.overlayEl.querySelectorAll('#assassinArsenal [data-weapon]').forEach((btn) => {
      btn.classList.toggle('on', btn.dataset.weapon === this.currentWeapon);
    });
  }

  setSword(angle, scale, reach) {
    const s = scale == null ? 1 : scale;
    const x = reach || 0;
    if (!this.overlayEl) return;
    this.overlayEl.querySelectorAll('.weapon.active').forEach((el) => {
      el.style.transform = `translateX(${x}px) rotate(${angle}deg) scale(${s})`;
    });
  }

  applyLeg(side, pose) {
    const b = this.bones[side];
    if (!b || !b.thigh) return;
    b.thigh.style.transform = `rotate(${pose.thigh}deg)`;
    b.shin.style.transform = `rotate(${pose.shin}deg)`;
    b.foot.style.transform = `rotate(${pose.foot}deg)`;
  }

  poseLegsFromTargets(left, right, legThigh = THIGH, legShin = SHIN) {
    this.applyLeg('L', solveLeg(left.x, left.y, -1, legThigh, legShin));
    this.applyLeg('R', solveLeg(right.x, right.y, -1, legThigh, legShin));
  }

  poseKiller({ x, lift, rotX, rotZ, scaleX, scaleY, bright }) {
    if (!this.killer) return;
    this.killer.style.left = '0px';
    this.killer.style.filter = `brightness(${bright ?? 1})`;
    this.killer.style.transform =
      `translateX(${x}px) translateY(${-(lift ?? 0)}px) rotateX(${rotX ?? 0}deg) rotateZ(${rotZ ?? 0}deg) scaleX(${scaleX ?? 1}) scaleY(${scaleY ?? 1})`;
  }

  poseVictim({ rotX, rotZ, x, y, scaleY, bright }) {
    if (!this.victim) return;
    this.victim.style.filter = `brightness(${bright ?? 0.92})`;
    this.victim.style.transform =
      `translateX(${x ?? 0}px) translateY(${y ?? 0}px) rotateX(${rotX ?? 76}deg) rotateZ(${rotZ ?? 0}deg) scaleY(${scaleY ?? 0.42})`;
  }

  resetScene() {
    cancelAnimationFrame(this.raf);
    this.running = false;
    if (!this.killer || !this.victim || !this.stage) return;

    const isMobile = (this.stage.clientWidth || 800) < 600;
    const scale = isMobile ? 0.5 : 1;

    this.killer.className = 'card';
    this.victim.className = 'card';
    this.stage.classList.remove('bloodied');
    this.poseLegsFromTargets({ x: -4 * scale, y: 8 * scale }, { x: 4 * scale, y: 8 * scale }, THIGH * scale, SHIN * scale);
    this.setSword(-12, 0.85, 0);

    this.overlayEl.querySelectorAll('.weapon').forEach((el) => el.classList.remove('firing'));
    const heldSpear = this.overlayEl.querySelector('#w-spear');
    if (heldSpear) heldSpear.style.opacity = '';
    if (this.spearFlight) {
      this.spearFlight.classList.remove('airborne');
      this.spearFlight.style.transform = '';
    }
    if (this.arrowFlight) {
      this.arrowFlight.classList.remove('airborne');
      this.arrowFlight.style.transform = '';
    }
    const w = this.stage.clientWidth || 800;
    this.poseKiller({
      x: w * START_LEFT,
      lift: 0,
      rotX: 76,
      rotZ: 0,
      scaleX: 1,
      scaleY: 0.42,
      bright: 0.92
    });
    this.poseVictim({ rotX: 76, rotZ: 0, x: 0, y: 0, scaleY: 0.42, bright: 0.92 });
  }

  play() {
    this.resetScene();
    this.running = true;

    if (this.safetyTimer) {
      clearTimeout(this.safetyTimer);
    }
    this.safetyTimer = setTimeout(() => {
      this.close(true);
    }, 10000);

    const self = this;
    const stage = this.stage;
    const killer = this.killer;
    const victim = this.victim;
    const spearFlight = this.spearFlight;
    const arrowFlight = this.arrowFlight;
    const currentWeapon = this.currentWeapon;

    const width = stage.clientWidth || 800;
    const isMobile = width < 600;
    const scale = isMobile ? 0.5 : 1;
    const homeX = width * START_LEFT;
    const victimX = width * 0.69;
    const CARD_W = 86 * scale;
    const CARD_H = 122 * scale;
    const THIGH_LEN = THIGH * scale;
    const SHIN_LEN = SHIN * scale;
    const STAND_LIFT_VAL = STAND_LIFT * scale;
    const STRIKE_GAP_VAL = (currentWeapon === 'dagger' ? 58 : STRIKE_GAP) * scale;
    const CARD_BOTTOM = 92 * scale;
    const FLOOR_PAD = 26 * scale;
    const arriveX = RANGED[currentWeapon]
      ? homeX + (victimX - homeX) * 0.5
      : victimX - STRIKE_GAP_VAL;
    let thrown = null;
    const cruise = WALK_SPEED * width;

    const body = {
      x: homeX,
      vx: 0,
      lift: 0,
      rotX: 76,
      rotZ: 0,
      scaleX: 1,
      scaleY: 0.42
    };

    const springLift = new Spring(3.2, 0.78, 0);
    const springRotZ = new Spring(4.4, 0.62, 0);
    const springRotX = new Spring(3.6, 0.7, 76);
    const springSquash = new Spring(8.5, 0.55, 0);
    const springBladeW = new Spring(7.5, 0.72, 0);
    const springBladeA = new Spring(6.5, 0.7, 0);

    const feet = {
      L: { worldX: homeX - 8 * scale, locked: true, lastLocalX: -6 * scale },
      R: { worldX: homeX + 8 * scale, locked: true, lastLocalX: 6 * scale }
    };

    const king = {
      live: true,
      ragdoll: null,
      lethal: false
    };
    let limp = null;
    const FLOOR_Y = () => stage.clientHeight - FLOOR_PAD;
    const kingBVH = new BVH();
    const legBVH = new BVH();
    const bvhLayer = this.bvhLayer;

    function drawBVH() {
      if (!bvhLayer || bvhLayer.classList.contains('hidden')) return;
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      bvhLayer.setAttribute('viewBox', `0 0 ${w} ${h}`);
      const boxes = kingBVH.boxes().map((b) => ({ ...b, tint: '#d9d2c0' }))
        .concat(legBVH.boxes().map((b) => ({ ...b, tint: '#b4232a' })));
      bvhLayer.innerHTML = boxes.map((b) => {
        const { x0, y0, x1, y1 } = b.aabb;
        const op = b.leaf ? 0.55 : Math.max(0.12, 0.4 - b.depth * 0.08);
        const sw = b.leaf ? 1.2 : 0.8;
        return `<rect x="${x0.toFixed(1)}" y="${y0.toFixed(1)}" width="${Math.max(0, x1 - x0).toFixed(1)}" height="${Math.max(0, y1 - y0).toFixed(1)}" fill="none" stroke="${b.tint}" stroke-opacity="${op}" stroke-width="${sw}"/>`;
      }).join('');
    }

    function resolveBroadphase(kingPts, legPts) {
      if (!kingBVH.root || kingBVH.leaves.length !== kingPts.length) kingBVH.build(kingPts);
      else kingBVH.refit();
      if (!legBVH.root || legBVH.leaves.length !== legPts.length) legBVH.build(legPts);
      else legBVH.refit();
      BVH.collide(kingBVH, legBVH, collideParticles);
      drawBVH();
    }

    function spawnKingRagdoll() {
      const left = stage.clientWidth * 0.69;
      const floor = FLOOR_Y();
      const bl = VPoint(left, floor, 1, 'king');
      const br = VPoint(left + CARD_W, floor, 1, 'king');
      const tl = VPoint(left, floor - CARD_H, 1, 'king');
      const tr = VPoint(left + CARD_W, floor - CARD_H, 1, 'king');
      const mid = VPoint(left + CARD_W / 2, floor - CARD_H / 2, 1.4, 'king');
      [bl, br, tl, tr, mid].forEach((p) => { p.radius = 10 * scale; });
      verletImpulse(tl, 18 * scale, -14 * scale);
      verletImpulse(bl, 28 * scale, -4 * scale);
      verletImpulse(mid, 16 * scale, -16 * scale);
      verletImpulse(tr, 8 * scale, -10 * scale);
      victim.classList.add('ragdolled');
      return {
        pts: [tl, tr, br, bl, mid],
        rods: [
          VRod(tl, tr, null, 1),
          VRod(tr, br, null, 1),
          VRod(br, bl, null, 1),
          VRod(bl, tl, null, 1),
          VRod(tl, br, null, 0.45),
          VRod(tr, bl, null, 0.45),
          VRod(mid, tl, null, 0.75),
          VRod(mid, tr, null, 0.75),
          VRod(mid, br, null, 0.75),
          VRod(mid, bl, null, 0.75)
        ]
      };
    }

    function stepKingRagdoll(dt) {
      const g = 2200;
      king.ragdoll.pts.forEach((p) => verletStep(p, dt, 0, g, 0.988));
      for (let i = 0; i < 8; i++) king.ragdoll.rods.forEach(solveRod);
      const floor = FLOOR_Y();
      const x0 = 18;
      const x1 = stage.clientWidth - 18;
      king.ragdoll.pts.forEach((p) => {
        collideFloor(p, floor, -0.28);
        collideWalls(p, x0, x1);
      });
      const [tl, tr, br, bl] = king.ragdoll.pts;
      const cx = (tl.x + tr.x + br.x + bl.x) / 4;
      const cy = (tl.y + tr.y + br.y + bl.y) / 4;
      const ang = Math.atan2(
        (tr.y + br.y) * 0.5 - (tl.y + bl.y) * 0.5,
        (tr.x + br.x) * 0.5 - (tl.x + bl.x) * 0.5
      );
      victim.style.filter = 'brightness(0.74)';
      victim.style.transform =
        `translate(${cx - CARD_W / 2}px, ${cy - CARD_H / 2}px) rotate(${ang * 180 / Math.PI}deg)`;
    }

    function seedLimpLegs(hipLift) {
      const hipY = stage.clientHeight - CARD_BOTTOM - hipLift;
      const cx = body.x + CARD_W / 2;
      function chain(sx, kickX) {
        const hip = VPoint(cx + sx * scale, hipY, 1, 'leg');
        hip.pin = true;
        const knee = VPoint(cx + (sx + 3) * scale, hipY + THIGH_LEN, 1, 'leg');
        const foot = VPoint(cx + (sx + 5) * scale, hipY + THIGH_LEN + SHIN_LEN, 1, 'leg');
        verletImpulse(foot, kickX * scale, 4 * scale);
        verletImpulse(knee, kickX * 0.4 * scale, 2 * scale);
        return {
          hip, knee, foot,
          rods: [VRod(hip, knee, THIGH_LEN, 1), VRod(knee, foot, SHIN_LEN, 1)]
        };
      }
      return { L: chain(-16, -6), R: chain(16, 9) };
    }

    function stepLimpLegs(hipLift, dt) {
      const hipY = stage.clientHeight - CARD_BOTTOM - hipLift;
      const cx = body.x + CARD_W / 2;
      limp.L.hip.x = cx - 16 * scale; limp.L.hip.y = hipY;
      limp.R.hip.x = cx + 16 * scale; limp.R.hip.y = hipY;
      const pts = [limp.L.hip, limp.L.knee, limp.L.foot, limp.R.hip, limp.R.knee, limp.R.foot];
      const rods = limp.L.rods.concat(limp.R.rods);
      pts.forEach((p) => verletStep(p, dt, 0, 1900, 0.984));
      for (let i = 0; i < 5; i++) rods.forEach(solveRod);
      const floor = FLOOR_Y();
      [limp.L.knee, limp.L.foot, limp.R.knee, limp.R.foot].forEach((p) => collideFloor(p, floor, -0.2));

      function angles(leg) {
        const thigh = Math.atan2(leg.knee.x - leg.hip.x, leg.knee.y - leg.hip.y);
        const shin = Math.atan2(leg.foot.x - leg.knee.x, leg.foot.y - leg.knee.y);
        return {
          thigh: thigh * 180 / Math.PI,
          shin: (shin - thigh) * 180 / Math.PI,
          foot: -shin * 180 / Math.PI
        };
      }
      self.applyLeg('L', angles(limp.L));
      self.applyLeg('R', angles(limp.R));
    }

    function resolveHit() {
      king.live = false;
      king.lethal = true;
      king.ragdoll = spawnKingRagdoll();
      limp = seedLimpLegs(body.lift || STAND_LIFT);
      victim.classList.add('hit');
      stage.classList.add('bloodied');
    }

    let phase = 0;
    let state = 'idle';
    let stateT = 0;
    let lastLift = 0;
    let doneHold = 0;
    let last = performance.now();

    function gaitFeet(hipX, hipLift, speed, dt) {
      const stepHz = clamp(0.7 + Math.abs(speed) / 90, 1.15, 2.6);
      phase += dt * stepHz;
      const travel = Math.abs(speed) * (0.5 / stepHz);
      const half = clamp(travel * 0.5, 10 * scale, 26 * scale);

      function one(side, offset) {
        const foot = feet[side];
        const cycle = (((phase + (side === 'R' ? 0.5 : 0)) % 1) + 1) % 1;
        const stance = cycle < 0.55;
        if (stance) {
          if (!foot.locked) {
            foot.locked = true;
            foot.worldX = hipX + half * Math.sign(speed || 1) + offset;
          }
          return {
            x: foot.worldX - hipX,
            y: hipLift
          };
        }
        foot.locked = false;
        const u = (cycle - 0.55) / 0.45;
        const swing = u * u * (3 - 2 * u);
        const takeoff = foot.worldX - hipX;
        const landing = half * Math.sign(speed || 1) + offset;
        return {
          x: lerp(takeoff, landing, swing),
          y: hipLift - Math.sin(u * Math.PI) * (STEP_LIFT * scale + Math.abs(speed) * 0.04)
        };
      }

      return {
        L: one('L', -5 * scale),
        R: one('R', 5 * scale),
        stepHz
      };
    }

    function frame(now) {
      if (!self.running) return;
      const dt = Math.min(0.04, (now - last) / 1000) || 0.016;
      last = now;
      stateT += dt;

      let wantLift = 0;
      let wantRotZ = 0;
      let wantRotX = 76;
      const kit = WEAPONS[currentWeapon];
      let wantBladeA = kit.carry;
      let wantBladeS = 0.9;
      let wantReach = 0;
      let wantSquash = 0;
      let wantVx = 0;
      let bright = 0.92;
      let mode = 'lie';

      if (state === 'idle') {
        const n = valueNoise(stateT * 3.1) * 1.8 + valueNoise(stateT * 7.4) * 0.7;
        body.x = homeX + n * 0.6;
        wantRotX = 76 + valueNoise(stateT * 2.2 + 8) * 2;
        wantSquash = 0.02 + valueNoise(stateT * 4.5) * 0.015;
        if (stateT > 0.85) {
          state = 'rise';
          stateT = 0;
        }
      } else if (state === 'rise') {
        const p = clamp(stateT / 1.15, 0, 1);
        wantLift = easeOut(p) * STAND_LIFT_VAL;
        wantRotX = lerp(76, 0, easeOut(p));
        wantRotZ = valueNoise(stateT * 5) * 1.2;
        wantSquash = Math.sin(p * Math.PI) * -0.08;
        bright = lerp(0.92, 1, p);
        mode = p > 0.45 ? 'stand' : 'lie';
        wantBladeA = lerp(-12, kit.carry, p);
        wantBladeS = lerp(0.7, 1, p);
        if (p > 0.45) killer.classList.add('standing');
        if (p >= 1) {
          state = 'walk';
          stateT = 0;
          body.lift = STAND_LIFT_VAL;
          feet.L.worldX = body.x - 10 * scale;
          feet.R.worldX = body.x + 12 * scale;
          feet.L.locked = true;
          feet.R.locked = true;
        }
      } else if (state === 'walk') {
        killer.classList.add('walking');
        const dist = arriveX - body.x;
        const slow = clamp(dist / 140, 0.22, 1);
        wantVx = cruise * slow;
        wantLift = STAND_LIFT_VAL;
        wantRotX = 0;
        wantRotZ = 0;
        bright = 1;
        mode = 'walk';
        wantBladeA = kit.carry + Math.sin(phase * Math.PI * 2) * 6;
        wantBladeS = 1;
        if (dist < 8) {
          state = 'coil';
          stateT = 0;
          body.vx *= 0.2;
        }
      } else if (state === 'coil') {
        killer.classList.remove('walking');
        killer.classList.add('attacking');
        const isThrow = !!RANGED[currentWeapon];
        wantVx = isThrow ? -18 * scale : -42 * scale;
        wantLift = STAND_LIFT_VAL + (isThrow ? 8 * scale : 14 * scale);
        wantRotX = isThrow ? -6 : -10;
        wantRotZ = isThrow ? -8 : -11;
        wantBladeA = kit.coil;
        wantBladeS = 1.05;
        wantReach = kit.reach * 0.15;
        wantSquash = 0.1;
        bright = 1;
        mode = 'coil';
        if (stateT > (isThrow ? 0.28 : 0.34)) {
          state = 'strike';
          stateT = 0;
          body.vx = isThrow ? 20 : 340;
          if (isThrow && !thrown) {
            const missile = currentWeapon === 'bow' ? arrowFlight : spearFlight;
            const held = self.overlayEl.querySelector(currentWeapon === 'bow' ? '#w-bow' : '#w-spear');
            if (held && currentWeapon === 'spear') held.style.opacity = '0';
            thrown = {
              el: missile,
              x0: body.x + (currentWeapon === 'bow' ? 62 : 70),
              y0: stage.clientHeight - 92 - STAND_LIFT - 52,
              x1: victimX + 20,
              y1: stage.clientHeight - 92 - 36,
              t: 0,
              dur: currentWeapon === 'bow' ? 0.32 : 0.42
            };
            if (missile) missile.classList.add('airborne');
          }
        }
      } else if (state === 'strike') {
        const isThrow = !!RANGED[currentWeapon];
        wantVx = isThrow ? 0 : 40;
        wantLift = STAND_LIFT - (isThrow ? 2 : 10);
        wantRotX = isThrow ? 4 : 8;
        wantRotZ = isThrow ? 6 : 13;
        wantBladeA = kit.strike;
        wantBladeS = isThrow ? 0 : 1.08;
        wantReach = kit.reach;
        wantSquash = isThrow ? 0.04 : -0.12;
        bright = 1;
        mode = 'strike';
        if (thrown) {
          thrown.t += dt;
          const u = clamp(thrown.t / thrown.dur, 0, 1);
          const ease = 1 - Math.pow(1 - u, 2);
          const x = lerp(thrown.x0, thrown.x1, ease);
          const arc = Math.sin(u * Math.PI) * 36;
          const y = lerp(thrown.y0, thrown.y1, ease) - arc;
          const ang = Math.atan2(thrown.y1 - thrown.y0, thrown.x1 - thrown.x0) * 180 / Math.PI;
          if (thrown.el) {
            thrown.el.style.left = `${x}px`;
            thrown.el.style.top = `${y}px`;
            thrown.el.style.transform = `rotate(${ang}deg)`;
          }
          if (u >= 0.78 && king.live) {
            resolveHit();
          }
          if (u >= 1) {
            state = 'recover';
            stateT = 0;
          }
        } else {
          if (king.live && stateT > 0.08) {
            resolveHit();
          }
          if (stateT > 0.28) {
            state = 'recover';
            stateT = 0;
          }
        }
      } else if (state === 'recover') {
        wantVx = 0;
        wantLift = STAND_LIFT;
        wantRotX = 0;
        wantRotZ = -6;
        wantBladeA = kit.recover;
        wantBladeS = 1;
        wantReach = 0;
        bright = 1;
        mode = 'stand';
        killer.classList.add('victory');
        if (stateT > 0.2 && king.lethal) victim.classList.add('dead');
        doneHold += dt;
        if (doneHold > 1.6) {
          self.running = false;
          self.autoCloseTimer = setTimeout(() => {
            self.close(true);
          }, 2000);
          return;
        }
      }

      if (state === 'walk' || state === 'coil' || state === 'strike' || state === 'recover') {
        body.vx = lerp(body.vx, wantVx, 1 - Math.exp(-dt * 8));
        body.x += body.vx * dt;
      }

      springLift.tick(wantLift, dt);
      springRotZ.tick(wantRotZ, dt);
      springRotX.tick(wantRotX, dt);
      springSquash.tick(wantSquash, dt);
      springBladeW.tick(wantBladeS, dt);
      springBladeA.tick(wantBladeA, dt);

      let hip = springLift.pos;
      let rotZ = springRotZ.pos;
      let targets = { L: { x: -4 * scale, y: Math.max(8 * scale, hip) }, R: { x: 4 * scale, y: Math.max(8 * scale, hip) } };

      if (mode === 'walk') {
        const bob = (Math.abs(Math.sin(phase * Math.PI * 2)) * 4.2 * scale)
          + Math.sin(phase * Math.PI * 4) * 0.8 * scale;
        hip = springLift.pos - 3 * scale + bob;
        rotZ += Math.sin(phase * Math.PI * 2) * 4.6;
        rotZ += Math.sin(phase * Math.PI * 4) * 1.1;
        targets = gaitFeet(body.x, hip, body.vx, dt);
      } else if (mode === 'coil') {
        targets = { L: { x: -6 * scale, y: hip - 9 * scale }, R: { x: 8 * scale, y: hip - 8 * scale } };
      } else if (mode === 'strike') {
        targets = { L: { x: 8 * scale, y: hip - 3 * scale }, R: { x: 16 * scale, y: hip - 2 * scale } };
      } else if (mode === 'stand') {
        targets = { L: { x: -5 * scale, y: hip }, R: { x: 5 * scale, y: hip } };
      }

      const dLift = (hip - lastLift) / Math.max(dt, 0.008);
      lastLift = hip;
      const squash = clamp(springSquash.pos + clamp(-dLift * 0.0018, -0.1, 0.12), -0.25, 0.25);

      if (limp) {
        stepLimpLegs(mode === 'lie' ? 0 : hip, dt);
      } else if (mode !== 'lie') {
        self.poseLegsFromTargets(targets.L, targets.R, THIGH_LEN, SHIN_LEN);
      }

      self.poseKiller({
        x: body.x,
        lift: mode === 'lie' ? 0 : hip,
        rotX: springRotX.pos,
        rotZ,
        scaleX: clamp(1 - squash * 0.55, 0.5, 1.5),
        scaleY: clamp((mode === 'lie' ? 0.42 : 1) + squash, 0.2, 1.8),
        bright
      });

      self.setSword(springBladeA.pos, Math.max(0.6, springBladeW.pos), wantReach);

      if (king.ragdoll) {
        stepKingRagdoll(dt);
      } else if (king.live) {
        const riseAmt = clamp((76 - springRotX.pos) / 76, 0, 1);
        self.poseVictim({
          rotX: springRotX.pos,
          rotZ: riseAmt * Math.sin((performance.now() / 1000) * 2.2) * 1.4,
          x: 0,
          y: -springLift.pos,
          scaleY: clamp(lerp(0.42, 1, riseAmt) + squash * 0.2, 0.2, 1.8),
          bright: lerp(0.92, 1, riseAmt)
        });
      }

      if (king.ragdoll && limp) {
        resolveBroadphase(
          king.ragdoll.pts,
          [limp.L.knee, limp.L.foot, limp.R.knee, limp.R.foot]
        );
      }

      self.raf = requestAnimationFrame(frame);
    }

    self.raf = requestAnimationFrame(frame);
  }

  close(triggerCallback = true) {
    if (this.safetyTimer) {
      clearTimeout(this.safetyTimer);
      this.safetyTimer = null;
    }
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    cancelAnimationFrame(this.raf);
    this.running = false;
    window.removeEventListener('keydown', this.handleKeyDown);

    if (this.overlayEl) {
      this.overlayEl.classList.remove('active');
      setTimeout(() => {
        if (this.overlayEl && this.overlayEl.parentNode) {
          this.overlayEl.parentNode.removeChild(this.overlayEl);
        }
        this.overlayEl = null;
      }, 300);
    }

    if (activeOverlayInstance === this) {
      activeOverlayInstance = null;
    }

    if (triggerCallback && typeof this.onComplete === 'function') {
      const cb = this.onComplete;
      this.onComplete = null;
      cb();
    }
  }
}
